// @ts-nocheck — Cloudscape components carry wide union-typed props (Badge color,
// Box color, StatusIndicator type); the sibling GoldenDatasetsV2Page opts out of
// strict checking for the same reason. The exported pure helpers below are still
// logically typed and unit-tested (gateOutcome/typeBadge/triggerLabel/verdictFor).
/**
 * PreDeploymentEvalTab — Offline (pre-deployment) evaluation: run list + detail
 * (summary, per-case explorer, and S3 HTML report), rendered with AWS UI
 * (Cloudscape) components to match the Eval console / Figma design.
 *
 * Pre-deployment runs are triggered by each code change and act as a release
 * gate: a change is only promoted once its test cases pass. INTEGRATION-TEST
 * runs (run_type=integration_test) are a different animal — a binary pass-rate
 * measurement with NO gate; the pipeline's Hydra approval workflow owns their
 * pass/fail. The display rules below keep those two apart.
 */
import { useState, useEffect, useCallback } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import Container from "@amzn/awsui-components-react/polaris/container";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Badge from "@amzn/awsui-components-react/polaris/badge";
import Link from "@amzn/awsui-components-react/polaris/link";
import Select from "@amzn/awsui-components-react/polaris/select";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import KeyValuePairs from "@amzn/awsui-components-react/polaris/key-value-pairs";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Pagination from "@amzn/awsui-components-react/polaris/pagination";
import CollectionPreferences from "@amzn/awsui-components-react/polaris/collection-preferences";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  listEvals,
  getEval,
  getEvalReport,
  ReportNotFoundError,
  sortNewestFirst,
} from "./preDeploymentApi";
import type { PreDeployRun } from "./preDeploymentApi";
import CaseExplorer from "./CaseExplorer";
import RetryRunAction from "./RetryRunAction";
import AskEvalAgent from "../../../components/AskEvalAgent/AskEvalAgent";
import "./PreDeploymentEvalTab.css";

// ─── Pure helpers (exported + unit-tested; rendering below consumes them) ─────

// API status describes run *execution* (did the pipeline finish), not the gate
// verdict — showing raw "SUCCESS" next to a "Blocked" gate read like a
// contradiction, so the label says Completed. Maps to a Cloudscape
// StatusIndicator type. Labels are translated (offline.status.<STATUS>); any other
// status is shown as the API sent it.
const STATUS_META: Record<string, { type: string; labelKey: string }> = {
  SUCCESS: { type: "success", labelKey: "offline.status.SUCCESS" },
  FAILED: { type: "error", labelKey: "offline.status.FAILED" },
  RUNNING: { type: "in-progress", labelKey: "offline.status.RUNNING" },
  // A deploy cut the run short — EM re-runs the batch next window, so from the
  // user's perspective it is still in flight, not an error.
  INTERRUPTED: { type: "pending", labelKey: "offline.status.INTERRUPTED" },
};

function StatusCell({ status }: { status: string }) {
  const { t } = useTranslation("eval");
  const meta = STATUS_META[status];
  return <StatusIndicator type={meta?.type ?? "info"}>{meta ? t(meta.labelKey) : status}</StatusIndicator>;
}

/** Category (skill / domain / …) as a Badge; a dash when the run has none
 *  (multi-item change-set runs leave it blank). */
function CategoryCell({ category }: { category?: string }) {
  if (!category) return <Box color="text-status-inactive">&mdash;</Box>;
  return <Badge color="grey">{category}</Badge>;
}

/** Release-gate outcome. "skipped" is a SUCCESS run that matched no test cases:
 *  it bypassed the gate and promoted, so it is neither passed-on-merit nor
 *  blocked — a distinct neutral state. "notPromoted" is a run that CLEARED the
 *  gate but published nothing (every target skipped/failed, e.g.
 *  INVALID_FRONTMATTER) — tests passed, yet the change did not ship, so it is
 *  neither promoted (green) nor blocked (gate failure). Kept as one helper so
 *  the list badge and the detail verdict never disagree. */
export type GateOutcome = "pending" | "skipped" | "promoted" | "notPromoted" | "blocked";
export function gateOutcome(run: {
  isComplete?: boolean;
  status?: string;
  isPassed?: boolean;
  passRate?: number;
  threshold?: number;
  totalTestCases?: number;
  promotionResults?: { status?: string }[];
}): GateOutcome {
  if (!run.isComplete) return "pending";
  // A deploy cut this run short. It is NOT a gate failure — EM reverts the
  // batch to PENDING and re-runs it next window — so it reads as still
  // evaluating, never "blocked" (mirrors the Java service's metrics bucketing).
  if (run.status === "INTERRUPTED") return "pending";
  if (run.status === "FAILED") return "blocked";
  // A completed, non-failed run that evaluated zero test cases skipped the gate
  // and promoted — show it as "Skipped", not "Blocked" (its passRate is 0).
  if (run.totalTestCases === 0) return "skipped";
  // Prefer the backend's explicit verdict; but integration-test runs don't
  // return `isPassed`, so fall back to passRate vs threshold (defaulting to a
  // full pass, since those runs are all-or-nothing) instead of mislabelling
  // every passing integration run as "blocked".
  const passed =
    typeof run.isPassed === "boolean"
      ? run.isPassed
      : run.passRate != null && run.passRate >= (run.threshold ?? 1);
  if (!passed) return "blocked";
  // Gate cleared — but "Promoted" must mean something actually SHIPPED. When the
  // run recorded per-target promotion_results and NONE reached "promoted" (all
  // skipped/failed — e.g. INVALID_FRONTMATTER), it passed the gate yet published
  // nothing → notPromoted. No promotionResults at all (legacy rows / in-flight)
  // falls back to the gate verdict → promoted, so historical runs are unchanged.
  const results = run.promotionResults;
  if (results && results.length > 0 && !results.some((r) => r.status === "promoted")) {
    return "notPromoted";
  }
  return "promoted";
}

/** Whether the detail view offers "Retry": a NORMAL change-set run the gate
 *  BLOCKED (below threshold, or the worker crashed → FAILED). Mirrors the eval
 *  API's eligibility (evaluation_retry_routes._ineligible) so the button only
 *  shows where the backend would accept it — the backend still has the final
 *  say and 409s otherwise. Reuses gateOutcome so the "Blocked" badge and the
 *  button can never disagree. */
export function canRetry(
  run: Parameters<typeof gateOutcome>[0] & { runType?: string; promotionMode?: string },
): boolean {
  return (
    run.runType !== "integration_test" &&
    run.promotionMode === "change_set" &&
    gateOutcome(run) === "blocked"
  );
}

// Gate outcome → Cloudscape Badge color. notPromoted is grey/neutral: tests
// passed but nothing shipped — "passed but idle", not an alarm (the brown amber
// warning token read badly), and distinct from green promoted / red blocked.
const GATE_BADGE_COLOR: Record<GateOutcome, string> = {
  promoted: "green",
  blocked: "red",
  pending: "blue",
  skipped: "grey",
  notPromoted: "grey",
};

/**
 * The Gate cell. Only NORMAL pre-deployment runs have a release gate. Integration
 * runs have NO gate — they neither promote nor block anything; the portal only
 * reports their pass rate and the pipeline's Hydra approval workflow owns the
 * pass/fail. So an integration row shows a plain dash.
 */
function ResultBadge({ run }: { run: PreDeployRun }) {
  const { t } = useTranslation("eval");
  if (run.runType === "integration_test") {
    return <Box color="text-status-inactive">&mdash;</Box>;
  }
  const outcome = gateOutcome(run);
  return <Badge color={GATE_BADGE_COLOR[outcome]}>{t(`offline.gate.${outcome}`)}</Badge>;
}

/** Run type → pill label + modifier. Pure (the label comes from the given `t`)
 *  so it can be unit-tested without rendering. Integration-test gate runs vs ordinary promotion runs. "Promotion"
 *  (not "Normal") names what these runs actually are — the change-driven /
 *  scoped release-gate runs that promote or block — so they read as clearly
 *  distinct from integration-test runs rather than a vague default. */
export function typeBadge(run: { runType?: string }, t: TFunction<"eval">): { label: string; modifier: string } {
  return run.runType === "integration_test"
    ? { label: t("offline.type.integration"), modifier: "integration" }
    : { label: t("offline.type.promotion"), modifier: "normal" };
}

/** Run type as a Cloudscape Badge. Integration = blue (attention), Normal =
 *  severity-neutral (muted) so the two read as clearly distinct — grey was too
 *  close to the Skipped gate badge. */
function TypeBadge({ run }: { run: PreDeployRun }) {
  const { t } = useTranslation("eval");
  const { label } = typeBadge(run, t);
  return (
    <Badge color={run.runType === "integration_test" ? "blue" : "severity-neutral"}>{label}</Badge>
  );
}

/** Fields the detail verdict banner reads. */
interface VerdictInput {
  runType?: string;
  isComplete?: boolean;
  status?: string;
  isPassed?: boolean;
  totalTestCases?: number;
  passRate?: number;
  threshold?: number;
  promotionResults?: { status?: string; reason?: string }[];
}

/** The first blocking reason across a run's targets (INVALID_FRONTMATTER /
 *  STALE / …), or "" when none is recorded — used to explain a notPromoted run
 *  in the banner subtitle. */
function firstBlockReason(run: VerdictInput): string {
  const blocked = (run.promotionResults ?? []).find((r) => r.status !== "promoted" && r.reason);
  return blocked?.reason ?? "";
}

/** The detail-view verdict banner content (class / glyph / title / subtitle).
 *  NORMAL runs get the release-gate verdict (promoted / notPromoted / blocked /
 *  skipped / pending). INTEGRATION runs have no gate — the banner is purely
 *  informational: it reports the measured pass rate and states that the binary
 *  pass/fail decision lives on the pipeline's approval workflow, NOT here. No
 *  ✓/✕ verdict, no threshold. Pure + exported (text comes from the given `t`) so
 *  both branches are unit-testable without rendering (mirrors gateOutcome). */
export function verdictFor(run: VerdictInput, t: TFunction<"eval">): {
  verdictClass: "passed" | "blocked" | "notPromoted" | "pending" | "info";
  verdictIcon: string;
  verdictTitle: string;
  verdictSub: string;
} {
  // Integration test: no release gate. Show the pass rate; the pipeline's
  // approval workflow owns the pass/fail decision, so the banner stays neutral.
  if (run.runType === "integration_test") {
    const measured = run.passRate != null;
    return {
      verdictClass: "info",
      verdictIcon: "◆",
      verdictTitle: measured ? t("offline.verdict.integrationMeasured") : t("offline.verdict.integrationRunning"),
      verdictSub: measured
        ? t("offline.verdict.integrationRate", { rate: (run.passRate! * 100).toFixed(1) })
        : t("offline.verdict.inProgress"),
    };
  }

  // Deploy-interrupted run: not a gate verdict at all. The batch goes back to
  // PENDING and re-runs in the next aggregation window under a new run_id, so
  // the banner says exactly that instead of a generic "in progress".
  if (run.status === "INTERRUPTED") {
    return {
      verdictClass: "pending",
      verdictIcon: "◌",
      verdictTitle: t("offline.verdict.interrupted"),
      verdictSub: t("offline.verdict.interruptedSub"),
    };
  }

  const rateSub =
    run.passRate != null && run.threshold != null
      ? t("offline.verdict.rate", {
          rate: (run.passRate * 100).toFixed(1),
          threshold: (run.threshold * 100).toFixed(0),
        })
      : t("offline.verdict.rateUnavailable");

  // Normal pre-deployment run: the release-gate verdict.
  const outcome = gateOutcome(run);

  // Gate cleared but nothing shipped: amber banner naming why, so it reads
  // consistently with the "Not promoted" list badge and the per-target table.
  if (outcome === "notPromoted") {
    const reason = firstBlockReason(run);
    return {
      verdictClass: "notPromoted",
      verdictIcon: "⚠",
      verdictTitle: t("offline.verdict.notPromoted"),
      verdictSub: reason
        ? t("offline.verdict.notPublishedReason", { reason })
        : t("offline.verdict.notPublished"),
    };
  }

  return {
    verdictClass:
      outcome === "promoted" ? "passed" : outcome === "blocked" ? "blocked" : "pending",
    verdictIcon:
      outcome === "promoted"
        ? "✓"
        : outcome === "blocked"
        ? "✕"
        : outcome === "skipped"
        ? "◔"
        : "◌",
    verdictTitle:
      outcome === "pending"
        ? t("offline.verdict.inProgress")
        : outcome === "skipped"
        ? t("offline.verdict.skipped")
        : outcome === "promoted"
        ? t("offline.verdict.promoted")
        : run.status === "FAILED"
        ? t("offline.verdict.failed")
        : t("offline.verdict.blocked"),
    verdictSub:
      outcome === "skipped"
        ? t("offline.verdict.skippedSub")
        : rateSub,
  };
}

/** Human label for the Trigger column. Integration-test runs are launched by
 *  the Hydra approval workflow, not a code-change / scoped promote, so they get
 *  their own label instead of falling through to "Scoped run". Exported for
 *  unit testing (mirrors gateOutcome). */
export function triggerLabel(run: { runType?: string; promotionMode?: string }, t: TFunction<"eval">): string {
  if (run.runType === "integration_test") return t("offline.trigger.approval");
  return run.promotionMode === "change_set" ? t("offline.trigger.codeChange") : t("offline.trigger.scoped");
}

// verdictClass → StatusIndicator type. notPromoted uses "stopped" (grey): tests
// passed but nothing shipped — a neutral halt, same grey as Skipped, not the
// brown "warning" glyph.
const VERDICT_STATUS: Record<string, string> = {
  passed: "success",
  blocked: "error",
  notPromoted: "stopped",
  pending: "in-progress",
  info: "info",
};
// verdictClass → Cloudscape Box text-status color token (not raw hex): stays on
// the design system, adapts to light/dark. notPromoted uses secondary/grey
// (the amber warning token renders brown) — "passed but nothing shipped" reads
// as neutral, not an alarm.
const VERDICT_RATE_COLOR: Record<string, string> = {
  passed: "text-status-success",
  blocked: "text-status-error",
  notPromoted: "text-body-secondary",
  pending: "text-status-info",
  info: "text-status-info",
};

/** Pass rate as a colored number. `neutral` (integration runs) uses info-blue —
 *  the number is a plain measurement, not a graded verdict, since an integration
 *  run has no threshold to grade against. */
function PassRate({
  rate,
  threshold,
  neutral = false,
}: {
  rate?: number;
  threshold?: number;
  neutral?: boolean;
}) {
  if (rate == null) return <Box color="text-status-inactive">&mdash;</Box>;
  let color = "text-status-error";
  if (neutral) color = "text-status-info";
  else if (rate >= (threshold ?? 0.85)) color = "text-status-success";
  else if (rate >= 0.7) color = "text-status-warning";
  return (
    <Box color={color} fontWeight="bold">
      {(rate * 100).toFixed(1)}%
    </Box>
  );
}

// Runs are operated from Hong Kong/Shanghai — show wall-clock time there instead
// of the UTC the API returns.
const DISPLAY_TIME_ZONE = "Asia/Shanghai";

/** A pass rate for an ask-the-agent question: "85.0%", or "—". */
function askRate(rate?: number | null): string {
  return rate != null ? `${(rate * 100).toFixed(1)}%` : "—";
}

/** What a run evaluated, as one line for an ask-the-agent question. */
function askTargets(run: PreDeployRun): string {
  const changes = (run.changes ?? []).filter((c) => c.name).map((c) => (c.category ? `${c.category}: ${c.name}` : c.name));
  if (changes.length > 0) return changes.join(", ");
  return (run.scope === "all" ? "all" : run.targetName || run.agentName) || "—";
}

function formatTime(iso?: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  // sv-SE renders as "YYYY-MM-DD HH:mm:ss" — ISO-like, no locale surprises.
  return date.toLocaleString("sv-SE", { timeZone: DISPLAY_TIME_ZONE });
}

/**
 * What the run evaluated: changed skills/domains (change-set runs) or the scope
 * target (scope runs). The API doesn't always return these — until the backend
 * ships them this renders nothing.
 */
function TargetSummary({ run }: { run: PreDeployRun }) {
  const { t } = useTranslation("eval");
  const changes = (run.changes ?? []).filter((c) => c.name);
  const scopeLabel =
    run.scope === "all" ? t("offline.allCapabilities") : run.targetName || run.agentName || null;

  if (changes.length === 0 && !scopeLabel) return null;

  return (
    <div>
      <Box variant="awsui-key-label">{changes.length > 0 ? t("offline.changedCapabilities") : t("offline.target")}</Box>
      <SpaceBetween direction="horizontal" size="xs">
        {changes.length > 0 ? (
          changes.map((c) => (
            <Badge key={`${c.category}:${c.name}`} color="blue">
              {c.category ? `${c.category}: ` : ""}
              {c.name}
            </Badge>
          ))
        ) : (
          <Badge color="blue">
            {run.scope ? `${run.scope}: ` : ""}
            {scopeLabel}
          </Badge>
        )}
      </SpaceBetween>
    </div>
  );
}

// Per-target promotion status → StatusIndicator type + label key.
const PROMOTION_STATUS_META: Record<string, { type: string; labelKey: string }> = {
  promoted: { type: "success", labelKey: "offline.promotion.status.promoted" },
  skipped: { type: "stopped", labelKey: "offline.promotion.status.skipped" },
  failed: { type: "error", labelKey: "offline.promotion.status.failed" },
};

/**
 * Per-target promotion breakdown: one row per target the run promoted, showing
 * whether it shipped / was skipped / failed and WHY. The run-level gate verdict
 * says whether the batch passed; this says what happened to each capability.
 * Renders nothing until the run has promoted (promotionResults absent before).
 */
function PromotionResults({ run }: { run: PreDeployRun }) {
  const { t } = useTranslation("eval");
  const results = run.promotionResults ?? [];
  if (results.length === 0) return null;

  return (
    <Container
      header={
        <Header variant="h2" actions={<AskEvalAgent question={t("askAgent.offline.promotion", { runId: run.runId })} />}>
          {t("offline.promotion.title")}
        </Header>
      }
    >
      <Table
        variant="embedded"
        items={results}
        columnDefinitions={[
          {
            id: "target",
            header: t("offline.promotion.colTarget"),
            cell: (r: any) => {
              const label = r.agentName ? `${r.agentName}/${r.name ?? ""}` : r.name || "—";
              return (
                <span>
                  {r.category ? (
                    <Badge color="blue">{r.category}</Badge>
                  ) : null}{" "}
                  {label}
                  <AskEvalAgent
                    question={t("askAgent.offline.promotionItem", {
                      runId: run.runId,
                      target: label,
                      status: r.status || "—",
                      note: [r.reason, r.error].filter(Boolean).join("; ") || "—",
                    })}
                  />
                </span>
              );
            },
          },
          {
            id: "status",
            header: t("offline.promotion.colStatus"),
            cell: (r: any) => {
              const meta = PROMOTION_STATUS_META[r.status];
              return <StatusIndicator type={meta?.type ?? "info"}>{meta ? t(meta.labelKey) : r.status}</StatusIndicator>;
            },
          },
          {
            id: "version",
            header: t("offline.promotion.colVersion"),
            cell: (r: any) => (r.version ? <Box variant="code">{r.version}</Box> : "—"),
          },
          {
            id: "note",
            header: t("offline.promotion.colNote"),
            cell: (r: any) => (
              <SpaceBetween size="xxs">
                {r.reason && <Box fontWeight="bold">{r.reason}</Box>}
                {r.error && (
                  <Box color="text-body-secondary" fontSize="body-s">
                    {r.error}
                  </Box>
                )}
                {!r.reason && !r.error && "—"}
              </SpaceBetween>
            ),
          },
        ]}
      />
    </Container>
  );
}

// Run-type filter options: "" = all, "normal" = exclude integration, else only
// integration. Filtered server-side so pagination stays correct. Defaults to
// "normal" so the list shows ordinary runs first; integration is opt-in.
// Labels are translated in the component (offline.runType.<key>).
const RUN_TYPE_OPTIONS = [
  { key: "all", value: "" },
  // Value stays "normal" (API contract — the service validates
  // {integration_test, normal}); only the display label reads "Promotion".
  { key: "normal", value: "normal" },
  { key: "integration", value: "integration_test" },
];

// ─── Main Tab Component ─────────────────────────────────────────────────────

// Runaway-loop backstop when fetching all pages for a filter — far above any
// realistic run count for a single run-type, not an expected limit.
const MAX_LIST_PAGES = 200;
// Default client-side page size for the numbered pager; user-adjustable via the
// table preferences (gear) button.
const PAGE_SIZE = 10;

// Page-size choices offered in the table preferences panel.
const PAGE_SIZE_CHOICES = [10, 20, 50];

export default function PreDeploymentEvalTab() {
  const { t } = useTranslation("eval");
  const runTypeOptions = RUN_TYPE_OPTIONS.map((o) => ({ label: t(`offline.runType.${o.key}`), value: o.value }));
  const pageSizeOptions = PAGE_SIZE_CHOICES.map((n) => ({ value: n, label: t("offline.pageSizeOption", { n }) }));
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState<PreDeployRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [runDetail, setRunDetail] = useState<PreDeployRun | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMissing, setReportMissing] = useState(false);

  const selectedRun = searchParams.get("run");
  const [runTypeFilter, setRunTypeFilter] = useState("normal");
  // Table preferences (page size); driven by the gear button, feeds useCollection.
  const [preferences, setPreferences] = useState({ pageSize: PAGE_SIZE });

  // Load ALL runs for the current filter, then client-paginate with a numbered
  // pager. The API cursor only goes forward (no page jumps), so numbered pages
  // require the full set client-side — same approach as CaseExplorer and the
  // Golden Datasets page. MAX_LIST_PAGES is a backstop, not an expected cap.
  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const all: PreDeployRun[] = [];
      let token: string | undefined = undefined;
      for (let page = 0; page < MAX_LIST_PAGES; page++) {
        const data = await listEvals(100, token, runTypeFilter || undefined);
        all.push(...data.items);
        token = data.nextToken;
        if (!token) break;
      }
      // API order is raw created_at-string order (mixed +08:00 / UTC) — re-sort by time.
      setRuns(sortNewestFirst(all));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [runTypeFilter]);

  // Load on mount and reload from the top whenever the run-type filter changes.
  // A single effect covers both — a separate mount ([]) effect would double the
  // initial fetch and race on the response.
  useEffect(() => {
    loadRuns();
  }, [runTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load (or reload) detail whenever ?run= changes, incl. deep links.
  useEffect(() => {
    if (selectedRun) viewDetail(selectedRun);
  }, [selectedRun]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewDetail = async (runId: string) => {
    setRunDetail(null);
    setDetailError("");
    setReportUrl(null);
    setReportMissing(false);
    setDetailLoading(true);
    setReportLoading(true);

    getEval(runId)
      .then(setRunDetail)
      .catch((e: any) => setDetailError(e.message))
      .finally(() => setDetailLoading(false));

    getEvalReport(runId)
      .then((data) => setReportUrl(data.reportUrl))
      .catch((e: any) => {
        if (e instanceof ReportNotFoundError) setReportMissing(true);
      })
      .finally(() => setReportLoading(false));
  };

  const openRun = (runId: string) => setSearchParams({ tab: "pre-deployment", run: runId });
  const backToList = () => {
    setRunDetail(null);
    setReportUrl(null);
    setSearchParams({ tab: "pre-deployment" });
  };

  // Client-side pagination over the loaded runs. Called unconditionally (before
  // the detail early-return) to respect the rules of hooks; only consumed by the
  // list view below.
  const { items: pagedRuns, paginationProps } = useCollection(runs, {
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
  });

  // ─── Run Detail View ───
  if (selectedRun) {
    return (
      <div className="eval-predeploy">
        <div className="eval-predeploy__back">
          <Button variant="link" iconName="angle-left" onClick={backToList}>
            {t("offline.backToList")}
          </Button>
        </div>

        {detailLoading ? (
          <StatusIndicator type="loading">{t("offline.loadingRun")}</StatusIndicator>
        ) : detailError ? (
          <Box color="text-status-error">{detailError}</Box>
        ) : runDetail ? (
          <SpaceBetween size="l">
            {/* Verdict banner */}
            {(() => {
              const { verdictClass, verdictTitle, verdictSub } = verdictFor(runDetail, t);
              return (
                <Container>
                  <ColumnLayout columns={2}>
                    <SpaceBetween size="xs">
                      <div>
                        <StatusIndicator type={VERDICT_STATUS[verdictClass]}>
                          {verdictTitle}
                        </StatusIndicator>
                        <AskEvalAgent
                          question={t("askAgent.offline.verdict", {
                            runId: runDetail.runId,
                            verdict: verdictTitle,
                            detail: verdictSub || "—",
                          })}
                        />
                      </div>
                      <Box color="text-body-secondary">{verdictSub}</Box>
                      {/* Gate-blocked change-set runs only; the API re-checks. */}
                      {canRetry(runDetail) && <RetryRunAction runId={runDetail.runId} />}
                    </SpaceBetween>
                    {runDetail.passRate != null && (
                      <Box textAlign="right">
                        <Box
                          variant="span"
                          fontSize="heading-xl"
                          fontWeight="bold"
                          color={VERDICT_RATE_COLOR[verdictClass]}
                        >
                          {(runDetail.passRate * 100).toFixed(1)}%
                        </Box>
                      </Box>
                    )}
                  </ColumnLayout>
                </Container>
              );
            })()}

            {/* Run overview */}
            {(() => {
              const isIntegration = runDetail.runType === "integration_test";
              const items = [
                { label: t("offline.overview.status"), value: <StatusCell status={runDetail.status} /> },
                // Gate + Threshold apply only to normal runs — integration has
                // no release gate and no threshold (the approval workflow owns
                // pass/fail), so both are dropped, leaving the measured rate.
                ...(!isIntegration
                  ? [{ label: t("offline.overview.gate"), value: <ResultBadge run={runDetail} /> }]
                  : []),
                {
                  label: t("offline.overview.passRate"),
                  value: (
                    <PassRate
                      rate={runDetail.passRate}
                      threshold={runDetail.threshold}
                      neutral={isIntegration}
                    />
                  ),
                },
                ...(!isIntegration
                  ? [
                      {
                        label: t("offline.overview.threshold"),
                        value:
                          runDetail.threshold != null
                            ? t("offline.overview.thresholdValue", { n: (runDetail.threshold * 100).toFixed(0) })
                            : "—",
                      },
                    ]
                  : []),
                { label: t("offline.overview.category"), value: <CategoryCell category={runDetail.category} /> },
                { label: t("offline.overview.trigger"), value: triggerLabel(runDetail, t) },
                { label: t("offline.overview.created"), value: formatTime(runDetail.createdAt) },
                { label: t("offline.overview.delivery"), value: runDetail.delivery || "—" },
              ];
              return (
                <Container
                  header={
                    <Header
                      variant="h2"
                      description={<Box variant="code">{runDetail.runId}</Box>}
                      actions={
                        <AskEvalAgent
                          question={t("askAgent.offline.overview", {
                            runId: runDetail.runId,
                            targets: askTargets(runDetail),
                            trigger: triggerLabel(runDetail, t),
                            status: runDetail.status || "—",
                            passRate: askRate(runDetail.passRate),
                            threshold: askRate(runDetail.threshold),
                          })}
                        />
                      }
                    >
                      {t("offline.overview.title")}
                    </Header>
                  }
                >
                  <SpaceBetween size="l">
                    <TargetSummary run={runDetail} />
                    <KeyValuePairs columns={4} items={items} />
                    {runDetail.s3Location && (
                      <div>
                        <Box variant="awsui-key-label">{t("offline.overview.artifacts")}</Box>
                        <SpaceBetween direction="horizontal" size="s">
                          <Box variant="code">{runDetail.s3Location}</Box>
                          {reportUrl && (
                            <Link external href={reportUrl}>
                              {t("offline.overview.openReport")}
                            </Link>
                          )}
                        </SpaceBetween>
                      </div>
                    )}
                  </SpaceBetween>
                </Container>
              );
            })()}

            {/* Per-target promotion breakdown (renders only after promote). */}
            <PromotionResults run={runDetail} />

            {/* Interactive per-case explorer (kept as-is; wrapped in a Container
                for the console look). */}
            <Container
              header={
                <Header
                  variant="h2"
                  actions={<AskEvalAgent question={t("askAgent.offline.cases", { runId: runDetail.runId })} />}
                >
                  {t("offline.testCases")}
                </Header>
              }
            >
              <CaseExplorer runId={runDetail.runId} />
            </Container>

            {/* Full HTML report from S3 — opened in a new tab (not inline) so the
                per-case view stays primary; fallback for pre-cutover runs. */}
            <Container header={<Header variant="h2">{t("offline.fullReport")}</Header>}>
              {reportLoading ? (
                <StatusIndicator type="loading">{t("offline.loadingReport")}</StatusIndicator>
              ) : reportUrl ? (
                <SpaceBetween size="s">
                  <Box color="text-body-secondary">
                    {t("offline.reportBody")}
                  </Box>
                  <Button href={reportUrl} target="_blank" iconAlign="right" iconName="external">
                    {t("offline.openFullReport")}
                  </Button>
                </SpaceBetween>
              ) : reportMissing ? (
                <Box color="text-body-secondary">
                  {t("offline.noStoredReport")}
                </Box>
              ) : (
                <Box color="text-body-secondary">{t("offline.reportUnavailable")}</Box>
              )}
            </Container>
          </SpaceBetween>
        ) : null}
      </div>
    );
  }

  // ─── Run List View ───
  // When filtered to integration runs only, Gate is always "—" (no release gate)
  // and Category is always blank — both carry no information, so drop them.
  const integrationOnly = runTypeFilter === "integration_test";

  const columnDefinitions = [
    {
      id: "runId",
      header: t("offline.col.runId"),
      // Run IDs are long UUIDs (e.g. integration-<uuid>) — give them ample room
      // so they don't truncate at the default width.
      width: 400,
      minWidth: 240,
      cell: (run: PreDeployRun) => (
        <>
          <Link
            onFollow={(e: any) => {
              e.preventDefault();
              openRun(run.runId);
            }}
          >
            {run.runId}
          </Link>
          <AskEvalAgent
            question={t("askAgent.offline.run", {
              runId: run.runId,
              type: typeBadge(run, t).label,
              status: run.status || "—",
              passRate: askRate(run.passRate),
            })}
          />
        </>
      ),
    },
    { id: "type", header: t("offline.col.type"), width: 150, cell: (run: PreDeployRun) => <TypeBadge run={run} /> },
    {
      id: "status",
      header: t("offline.col.status"),
      width: 160,
      cell: (run: PreDeployRun) => <StatusCell status={run.status} />,
    },
    ...(!integrationOnly
      ? [
          {
            id: "gate",
            header: t("offline.col.gate"),
            width: 150,
            cell: (run: PreDeployRun) => <ResultBadge run={run} />,
          },
        ]
      : []),
    {
      id: "passRate",
      header: t("offline.col.passRate"),
      width: 140,
      cell: (run: PreDeployRun) => (
        <PassRate
          rate={run.passRate}
          threshold={run.threshold}
          neutral={run.runType === "integration_test"}
        />
      ),
    },
    ...(!integrationOnly
      ? [
          {
            id: "category",
            header: t("offline.col.category"),
            width: 150,
            cell: (run: PreDeployRun) => <CategoryCell category={run.category} />,
          },
        ]
      : []),
    { id: "trigger", header: t("offline.col.trigger"), width: 170, cell: (run: PreDeployRun) => triggerLabel(run, t) },
    {
      id: "created",
      header: t("offline.col.created"),
      width: 200,
      cell: (run: PreDeployRun) => formatTime(run.createdAt),
    },
  ];

  return (
    <div className="eval-predeploy">
      <Table
        variant="container"
        items={pagedRuns}
        loading={loading}
        loadingText={t("offline.loadingRuns")}
        columnDefinitions={columnDefinitions}
        resizableColumns
        trackBy="runId"
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title={t("offline.preferences")}
            confirmLabel={t("offline.confirm")}
            cancelLabel={t("offline.cancel")}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={{
              title: t("offline.runsPerPage"),
              options: pageSizeOptions,
            }}
          />
        }
        header={
          <Header
            counter={runs.length ? `(${runs.length})` : undefined}
            description={t("offline.description")}
            actions={
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <AskEvalAgent question={t("askAgent.offline.runs")} />
                <Button iconName="refresh" onClick={() => loadRuns()} loading={loading}>
                  {t("offline.refresh")}
                </Button>
              </SpaceBetween>
            }
          >
            {t("offline.header")}
          </Header>
        }
        filter={
          <div className="eval-predeploy__filters">
            <Select
              selectedOption={
                runTypeOptions.find((o) => o.value === runTypeFilter) || runTypeOptions[0]
              }
              onChange={({ detail }) => setRunTypeFilter(detail.selectedOption.value || "")}
              options={runTypeOptions}
              disabled={loading}
              ariaLabel={t("offline.filterRunType")}
            />
          </div>
        }
        empty={
          <Box textAlign="center" color="inherit">
            <b>{t("offline.noRuns")}</b>
            <Box variant="p" color="inherit">
              {t("offline.noRunsBody")}
            </Box>
          </Box>
        }
        footer={error ? <Box color="text-status-error">{error}</Box> : undefined}
      />
    </div>
  );
}
